import React from 'react';
import { ArrowUpNarrowWide, ChevronDown, Filter } from 'lucide-react';
import {
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'shared-portal-components-v2';

import type { DownloadFilterKey, FileSortDir, FileSortKey } from '@/types/SixDetail.types';

interface SixDetailFileBrowserControlsProps {
  searchQuery: string;
  downloadFilterKey: DownloadFilterKey;
  onSearchChange: (query: string) => void;
  onDownloadFilterChange: (filterKey: DownloadFilterKey) => void;
  sortKey: FileSortKey;
  sortDir: FileSortDir;
  onSortChange: (key: FileSortKey, dir: FileSortDir) => void;
  searchPlaceholder: string;
}

const SORT_OPTIONS: Array<{ key: FileSortKey; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'size', label: 'Size' },
  { key: 'modified', label: 'Modified' },
];

const FILTER_OPTIONS = [
  { key: 'allFiles', label: 'All Files' },
  { key: 'downloaded', label: 'Downloaded' },
  { key: 'notDownloaded', label: 'Not Downloaded' },
] as const;

const FILE_SORT_KEYS: readonly FileSortKey[] = SORT_OPTIONS.map((opt) => opt.key);
const DOWNLOAD_FILTER_KEYS: readonly DownloadFilterKey[] = FILTER_OPTIONS.map((opt) => opt.key);

function isFileSortKey(value: string): value is FileSortKey {
  return (FILE_SORT_KEYS as readonly string[]).includes(value);
}

function isDownloadFilterKey(value: string): value is DownloadFilterKey {
  return (DOWNLOAD_FILTER_KEYS as readonly string[]).includes(value);
}

function SixDetailFileBrowserControls({
  searchQuery,
  downloadFilterKey,
  onSearchChange,
  onDownloadFilterChange,
  sortKey,
  sortDir,
  onSortChange,
  searchPlaceholder,
}: SixDetailFileBrowserControlsProps): React.ReactElement {
  function handleSortSelect(nextSortKey: string): void {
    if (!isFileSortKey(nextSortKey)) return;
    const nextSortDir = nextSortKey === sortKey
      ? (sortDir === 'asc' ? 'desc' : 'asc')
      : 'asc';

    onSortChange(nextSortKey, nextSortDir);
  }

  return (
    <div className="mb-4 flex flex-col gap-4">
      <div className="flex-1">
        <SearchInput
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          onClear={() => {
            onSearchChange('');
          }}
        />
      </div>

      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Select
            value={downloadFilterKey}
            onValueChange={(nextFilterKey) => {
              if (isDownloadFilterKey(nextFilterKey)) {
                onDownloadFilterChange(nextFilterKey);
              }
            }}
          >
            <SelectTrigger className="h-11 w-full justify-between pl-2 pr-3 sm:w-40">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="All Files" />
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={sortKey}
            onValueChange={handleSortSelect}
          >
            <SelectTrigger className="h-11 w-full justify-between pl-2 pr-3 sm:w-40">
              <div className="flex items-center gap-2">
                <ArrowUpNarrowWide className="h-4 w-4" />
                <SelectValue placeholder="Sort by" />
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default SixDetailFileBrowserControls;
